import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getSubscriptionSummary, getFreeDownloadBadge } from '../../utils/subscription';
import { useResetCountdown } from '../../hooks/useResetCountdown';
import CreditStatusSummary from '../common/CreditStatusSummary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCreditHistory } from '../../hooks/useCreditHistory';
import { CreditChart } from '../credits';

const SettingsUsage = () => {
  const { userProfile } = useAuth();
  const { shortFormat: countdownText } = useResetCountdown();
  const { t, i18n } = useTranslation('settings');
  const {
    history,
    chartData,
    loading: historyLoading,
    periodDays,
    updatePeriod,
  } = useCreditHistory({ periodDays: '30' });

  const usageMetrics = useMemo(
    () => ({
      creditsRemaining: userProfile?.saas_usage?.credits_remaining ?? 0,
      creditsSpent: userProfile?.saas_usage?.credits_spent_total ?? 0,
      usageCount: userProfile?.saas_usage?.usage_count ?? 0,
      noWatermarkCount: userProfile?.saas_usage?.quota_no_watermark_count_today ?? 0,
      lastLogin: userProfile?.tracking?.last_login,
      lastDevice: userProfile?.tracking?.last_device,
      onboardingStatus: userProfile?.tracking?.onboarding_status || t('usage.undefined'),
      subscriptionPlan: userProfile?.saas_usage?.subscription_plan_id || t('usage.none'),
      subscriptionActive: Boolean(userProfile?.saas_usage?.subscription_active),
      totalUpscale: userProfile?.tracking?.total_upscale ?? 0,
      totalNoWatermark: userProfile?.tracking?.total_no_watermark ?? 0,
      lastNoWatermarkUse: userProfile?.saas_usage?.quota_no_watermark_last_use,
    }),
    [userProfile, t]
  );

  const subscriptionSummary = useMemo(() => getSubscriptionSummary(userProfile), [userProfile]);
  
  const freeDownloadBadge = useMemo(
    () => getFreeDownloadBadge(subscriptionSummary, countdownText),
    [subscriptionSummary, countdownText]
  );

  const spendingRows = useMemo(() => {
    return [...(history || [])]
      .filter((tx) => Number(tx.amount) !== 0)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 8);
  }, [history]);

  const formatAmount = (amount) => {
    if (amount == null) return '—';
    const signed = Number(amount) > 0 ? `+${amount}` : `${amount}`;
    return `${signed} cr`;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '—';
    try {
      return new Date(timestamp).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch (_err) {
      return timestamp;
    }
  };

  const getTypeLabel = (type) => t(`usage.types.${type || 'DEFAULT'}`, { defaultValue: type?.replace(/_/g, ' ') || '—' });

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-gray-800 pb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{t('usage.title')}</h2>
          <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
            {t('usage.subtitle')}
          </p>
        </div>
        <div className="hidden sm:block">
            <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                <FontAwesomeIcon icon="chart-pie" className="text-xl" />
            </div>
        </div>
      </div>

      <div className="space-y-6">
        <CreditStatusSummary
            variant="compact"
            credits={usageMetrics.creditsRemaining}
            planLabel={subscriptionSummary.planLabel}
            planType={subscriptionSummary.planType}
            subscriptionStatus={subscriptionSummary.subscriptionStatus}
            monthlyCredits={subscriptionSummary.monthlyCredits}
            creditsUsedThisCycle={subscriptionSummary.creditsUsedThisCycle}
            monthlyPrice={subscriptionSummary.monthlyPrice}
            nextBilling={subscriptionSummary.nextBilling}
            isUnlimited={subscriptionSummary.isUnlimited}
            freeDownloadBadge={freeDownloadBadge}
            showActions={false}
            className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 shadow-sm"
        />

        <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6 flex flex-col items-center text-center hover:border-amber-300/50 transition-colors">
                <div className="mb-3 h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
                    <FontAwesomeIcon icon="coins" />
                </div>
                <p className="text-sm font-medium uppercase tracking-wide text-slate-500 dark:text-gray-500">{t('usage.creditsSpent')}</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{usageMetrics.creditsSpent}</p>
                <p className="text-xs text-slate-400 mt-2">{t('usage.totalSinceSignup')}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6 flex flex-col items-center text-center hover:border-sky-300/50 transition-colors">
                <div className="mb-3 h-10 w-10 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-sky-600 dark:text-sky-400">
                    <FontAwesomeIcon icon="wand-magic-sparkles" />
                </div>
                <p className="text-sm font-medium uppercase tracking-wide text-slate-500 dark:text-gray-500">{t('usage.jobsProcessed')}</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{usageMetrics.usageCount}</p>
                <p className="text-xs text-slate-400 mt-2">{t('usage.allWorkflows')}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6 flex flex-col items-center text-center hover:border-purple-300/50 transition-colors">
                <div className="mb-3 h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                    <FontAwesomeIcon icon="download" />
                </div>
                <p className="text-sm font-medium uppercase tracking-wide text-slate-500 dark:text-gray-500">{t('usage.noWatermarkDay')}</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{usageMetrics.noWatermarkCount}</p>
                <p className="text-xs text-slate-400 mt-2">{t('usage.quotaUsedToday')}</p>
            </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {t('usage.creditEvolution', 'Évolution des crédits')}
              </h3>
              <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                {t('usage.creditEvolutionSubtitle', 'Solde, crédits acquis/dépensés')}
              </p>
            </div>
          </div>
          <CreditChart
            data={chartData}
            period={periodDays}
            onPeriodChange={updatePeriod}
            isLoading={historyLoading}
          />
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('usage.creditMovementsTitle')}</h3>
              <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">{t('usage.creditMovementsSubtitle')}</p>
            </div>
            <span className="text-xs font-medium px-3 py-1 rounded-full bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300">
              {t('usage.recent', { count: spendingRows.length })}
            </span>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-gray-800">
            <div className="grid grid-cols-12 bg-slate-50 dark:bg-slate-900/60 text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wide px-4 py-3">
              <div className="col-span-4">{t('usage.table.date')}</div>
              <div className="col-span-3">{t('usage.table.type')}</div>
              <div className="col-span-3">{t('usage.table.details')}</div>
              <div className="col-span-2 text-right">{t('usage.table.amount')}</div>
            </div>

            {spendingRows.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-900">
                {historyLoading ? t('usage.loading') : t('usage.emptyHistory')}
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-gray-800 bg-white dark:bg-slate-900">
                {spendingRows.map((tx) => {
                  const isPositive = Number(tx.amount) > 0;
                  return (
                    <div
                      key={tx.id}
                      className="grid grid-cols-12 items-center px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                    >
                      <div className="col-span-4 font-medium text-slate-900 dark:text-white">{formatDateTime(tx.timestamp)}</div>
                      <div className="col-span-3 flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${isPositive ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        <span>{getTypeLabel(tx.transaction_type)}</span>
                      </div>
                      <div className="col-span-3 text-slate-500 dark:text-slate-400 truncate" title={tx.description}>{tx.description || t('usage.noDescription')}</div>
                      <div className={`col-span-2 text-right font-semibold tabular-nums ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}`}>
                        {formatAmount(tx.amount)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsUsage;
